import type {
  ComponentType,
  CSSProperties,
  DragEvent,
  KeyboardEvent,
  MouseEvent,
  ReactNode
} from 'react';
import {formatDistanceToNow} from 'date-fns';
import {
  CalendarClock,
  ChevronRight,
  Clock,
  Film,
  Folder as FolderIcon,
  Globe,
  ImagePlus,
  LayoutGrid,
  ListVideo,
  MoreHorizontal,
  MoreVertical,
  Pin,
  Search,
  Telescope,
  Tv,
  User,
  Users
} from 'lucide-react';
import {
  Button,
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
  ContextMenuTrigger,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger
} from '@kroll-resolver-crisp/ui-components-react';
import type {Folder} from '@/api/folders';
import {jobDisplayLabel, type Job} from '@/api/jobs';
import {ITEM_NOUN_PLURAL} from '@/lib/platform';
import {jobStatusLabel, jobStatusTone, type StatusTone} from '@/lib/status';
import {StatusIcon} from '@/components/shared/StatusIcon';
import {describeCron} from '@/components/shared/ScheduleInput';
import {cn} from '@/lib/utils';

// The investigations rail's tree: its folder rows and investigation cards, and the status and
// collection-type vocabulary they draw. Pulled out of InvestigationsWorkspace so each row renders
// from props alone, which is what lets a story mount one without the whole workspace.

export type StatusConfig = {
  label: string;
  tone: StatusTone;
};

export function getStatusConfig(status: string): StatusConfig {
  return {label: jobStatusLabel(status), tone: jobStatusTone(status)};
}

// The rail's folder rows and investigation cards share one visual language, from the design's
// folder tree: a 28px row that lights up on hover, and an indented stack of bordered cards.
export const RAIL_ROW =
  'group flex min-h-7 cursor-pointer items-center gap-1.5 border-l-2 pr-2 text-xs font-medium transition-colors select-none';
export const RAIL_ROW_IDLE = 'hover:bg-muted focus-within:bg-muted border-transparent';
export const RAIL_ROW_ACTIVE = 'bg-accent border-primary';
export const RAIL_ROW_DROP = 'bg-primary/10 border-primary';
const RAIL_COUNT =
  'text-muted-foreground ml-auto shrink-0 pl-2 text-[11px] font-normal whitespace-nowrap tabular-nums';
// Nested rows and card stacks step in by this much per level of folder nesting.
const RAIL_INDENT = 24;
// A card stack sits under its folder's name, clear of the chevron and folder icon.
export const RAIL_CARDS_INSET = 32;

export function railIndent(depth: number, base: number): CSSProperties {
  return {paddingLeft: base + depth * RAIL_INDENT};
}

// Every row offers the same actions twice - from its "more" button and from a right-click - so the
// items are written once and told which menu family to render in.
type RailMenuItemProps = {
  readonly variant?: 'default' | 'destructive';
  readonly disabled?: boolean;
  readonly title?: string;
  readonly onClick?: (e: MouseEvent) => void;
  readonly children: ReactNode;
};
type RailSubmenuProps = {
  readonly className?: string;
  readonly children: ReactNode;
};
export type RailMenu = {
  readonly Item: ComponentType<RailMenuItemProps>;
  readonly Separator: ComponentType;
  readonly Sub: ComponentType<{readonly children: ReactNode}>;
  readonly SubTrigger: ComponentType<RailSubmenuProps>;
  readonly SubContent: ComponentType<RailSubmenuProps>;
};
export const DROPDOWN_MENU: RailMenu = {
  Item: DropdownMenuItem,
  Separator: DropdownMenuSeparator,
  Sub: DropdownMenuSub,
  SubTrigger: DropdownMenuSubTrigger,
  SubContent: DropdownMenuSubContent
};
export const CONTEXT_MENU: RailMenu = {
  Item: ContextMenuItem,
  Separator: ContextMenuSeparator,
  Sub: ContextMenuSub,
  SubTrigger: ContextMenuSubTrigger,
  SubContent: ContextMenuSubContent
};

export function investigationCountLabel(n: number): string {
  return `${n.toLocaleString()} investigation${n === 1 ? '' : 's'}`;
}

// A rail row's count as the number and the nav's Investigations icon, which reads cleaner down the
// rail than repeating the word on every row. The words stay for assistive tech and on hover.
export function RailInvestigationCount({count}: {readonly count: number}) {
  const label = investigationCountLabel(count);
  return (
    <span className={cn(RAIL_COUNT, 'inline-flex items-center gap-1')} title={label}>
      <span aria-hidden="true">{count.toLocaleString()}</span>
      <Telescope className="size-3" aria-hidden="true" />
      <span className="sr-only">{label}</span>
    </span>
  );
}

/** Who made the folder and what is in it. The full email, not the job tooltip's local part, so an admin can tell two analysts apart. */
export function folderRowTooltip(folder: Folder): string {
  return [
    folder.name,
    folder.created_by ? `Created by ${folder.created_by}` : null,
    `${formatDistanceToNow(new Date(folder.created_at))} ago`,
    investigationCountLabel(folder.job_count),
    folder.total_job_count > folder.job_count
      ? `${investigationCountLabel(folder.total_job_count)} including subfolders`
      : null
  ]
    .filter(Boolean)
    .join(' · ');
}

/** The card shows the label, count and author; the rest of what the old second line said lives here. */
export function jobRowTooltip(job: Job): string {
  return [
    jobDisplayLabel(job),
    job.intent,
    job.language,
    `${job.item_count.toLocaleString()} ${ITEM_NOUN_PLURAL}`,
    `${formatDistanceToNow(new Date(job.created_at))} ago`,
    job.created_by ? job.created_by.split('@')[0] : null
  ]
    .filter(Boolean)
    .join(' · ');
}

export type JobTypeConfig = {
  label: string;
  icon: typeof Search;
};

const DEFAULT_JOB_TYPE_CONFIG: JobTypeConfig = {label: 'Text', icon: Search};

// Keyed by Job.strategy. youtube_video (Single Video Submission) deliberately has no entry here -
// it's a separate workflow, not a collection type an analyst picks in this view, so it falls back
// to the default Query styling with no dedicated filter chip. Every strategy has an entry
// regardless of platform (a Falcon deployment can still hold legacy youtube_* rows, and vice
// versa) - JOB_TYPE_FILTERS below is what's actually platform-scoped.
export const JOB_TYPE_CONFIG: Record<string, JobTypeConfig> = {
  youtube_search: DEFAULT_JOB_TYPE_CONFIG,
  image_search: {label: 'Image', icon: ImagePlus},
  google_search: {label: 'Web search', icon: Globe},
  youtube_playlist: {label: 'Playlist', icon: ListVideo},
  // "Channel URL"/"Channel search" (not "Channel"/"Channels") so the two read as distinct job
  // types at a glance instead of a singular/plural pair.
  youtube_channel: {label: 'Channel URL', icon: Tv},
  youtube_channel_search: {label: 'Channel search', icon: Users},
  pinterest_profile: {label: 'Profile', icon: User},
  pinterest_profile_followers: {label: 'Followers', icon: Users},
  pinterest_profile_followers_of_followers: {label: 'Followers of followers', icon: Users},
  pinterest_board: {label: 'Board', icon: LayoutGrid},
  pinterest_search: {label: 'Profile search', icon: Search},
  pinterest_profile_pins: {label: 'Profile pins', icon: Pin},
  pinterest_profile_boards: {label: 'Board crawl', icon: LayoutGrid},
  pinterest_pin_search: {label: 'Pin search', icon: Search}
};

export function getJobTypeConfig(strategy: string): JobTypeConfig {
  return JOB_TYPE_CONFIG[strategy] ?? DEFAULT_JOB_TYPE_CONFIG;
}

/**
 One investigation in the rail: a bordered card that selects on click or Enter/Space, drags into
 a folder, and offers the same actions from its "more" button and a right-click. The workspace
 owns every piece of state; the card only reports what happened.
 */
export function InvestigationTreeRow({
  job,
  isSelected = false,
  isChecked = false,
  isDragging = false,
  menuItems,
  onActivate,
  onDragStart,
  onDragEnd
}: {
  readonly job: Job;
  readonly isSelected?: boolean;
  readonly isChecked?: boolean;
  readonly isDragging?: boolean;
  /** The row's actions, rendered once into its dropdown and once into its context menu. */
  readonly menuItems: (menu: RailMenu) => ReactNode;
  readonly onActivate?: (event: MouseEvent | KeyboardEvent) => void;
  readonly onDragStart?: (event: DragEvent) => void;
  readonly onDragEnd?: () => void;
}) {
  const statusInfo = getStatusConfig(job.status);
  const typeInfo = getJobTypeConfig(job.strategy);
  const TypeIcon = typeInfo.icon;
  const label = jobDisplayLabel(job);

  return (
    <ContextMenu>
      <ContextMenuTrigger
        draggable
        className={cn(
          'group/job bg-card text-foreground border-border hover:border-muted-foreground relative block overflow-hidden rounded-lg border transition-[border-color,box-shadow]',
          isSelected && 'border-primary bg-accent hover:border-primary',
          isChecked && !isSelected && 'bg-primary/10 border-primary/50',
          isDragging && 'opacity-40'
        )}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}>
        {/* Not a <button>: Firefox won't start a drag from a button inside a draggable card. */}
        <div
          role="button"
          tabIndex={0}
          aria-current={isSelected ? 'true' : undefined}
          title={statusInfo.label}
          className="focus-visible:outline-ring flex w-full cursor-pointer flex-col gap-1.5 py-2.5 pr-9 pl-3 text-left focus-visible:outline-2 focus-visible:-outline-offset-2"
          onClick={(e) => {
            onActivate?.(e);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onActivate?.(e);
            }
          }}>
          <span className="flex items-start gap-2 text-xs leading-[1.35] font-medium">
            <span className="mt-px flex shrink-0">
              <StatusIcon
                tone={statusInfo.tone}
                label={statusInfo.label}
                busy={job.status === 'running'}
              />
            </span>
            <span
              title={jobRowTooltip(job)}
              className={cn(
                'line-clamp-2 text-pretty',
                job.status === 'timed_out' && 'text-muted-foreground line-through'
              )}>
              {label}
            </span>
          </span>
          <span className="text-muted-foreground flex flex-wrap gap-x-3 gap-y-1 pl-[22px] text-[11px] tabular-nums">
            <span className="inline-flex items-center gap-1">
              <TypeIcon aria-hidden className="size-3 shrink-0" />
              {typeInfo.label}
            </span>
            {job.schedule_cron ? (
              <span
                className="inline-flex items-center"
                title={`${describeCron(job.schedule_cron)}${job.schedule_active ? '' : ' · Paused'}`}>
                <CalendarClock
                  aria-label={job.schedule_active ? 'Scheduled' : 'Schedule paused'}
                  className="size-3 shrink-0"
                />
              </span>
            ) : null}
            <span className="inline-flex items-center gap-1">
              <Film aria-hidden className="size-3 shrink-0" />
              {job.item_count.toLocaleString()} {ITEM_NOUN_PLURAL}
            </span>
            <span className="inline-flex items-center gap-1">
              <Clock aria-hidden className="size-3 shrink-0" />
              {formatDistanceToNow(new Date(job.created_at))} ago
            </span>
          </span>
          <span className="text-muted-foreground pl-[22px] text-[11px]">
            {job.status === 'failed' ? (
              <span className="text-destructive-ink">Run failed · </span>
            ) : null}
            {job.created_by.split('@')[0]}
          </span>
        </div>
        <span className="absolute top-1.5 right-1.5">
          <DropdownMenu>
            <DropdownMenuTrigger
              aria-label={`Actions for ${label}`}
              title={`Actions for ${label}`}
              render={<Button variant="ghost" size="icon-sm" className="text-foreground size-6" />}
              onClick={(e) => {
                e.stopPropagation();
              }}>
              <MoreVertical className="size-4" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              {menuItems(DROPDOWN_MENU)}
            </DropdownMenuContent>
          </DropdownMenu>
        </span>
      </ContextMenuTrigger>
      <ContextMenuContent className="w-48">{menuItems(CONTEXT_MENU)}</ContextMenuContent>
    </ContextMenu>
  );
}

/** How a folder row is lit: a drop target over everything, then the selected folder. */
export type FolderRowState = 'idle' | 'active' | 'drop';

const FOLDER_ROW_STATE: Record<FolderRowState, string> = {
  idle: RAIL_ROW_IDLE,
  active: RAIL_ROW_ACTIVE,
  drop: RAIL_ROW_DROP
};

/**
 The design's folder row: chevron, icon, name, count, one menu button, and the same menu on
 right-click. Clicking the row opens the folder's aggregate view; the chevron is what collapses it.
 While the folder is being renamed the workspace passes its input as `rename`, which takes the
 name's place.
 */
export function FolderTreeRow({
  name,
  depth,
  count,
  isExpanded,
  state = 'idle',
  refusesDrop = false,
  rename,
  tooltip,
  menuItems,
  onToggle,
  onSelect,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDragLeave,
  onDrop
}: {
  readonly name: string;
  readonly depth: number;
  readonly count: number;
  readonly isExpanded: boolean;
  readonly state?: FolderRowState;
  /** A folder can't be dropped into itself or its own subtree; the row dims while one is dragged. */
  readonly refusesDrop?: boolean;
  readonly rename?: ReactNode;
  /** Hover text for the name; falls back to the name itself. */
  readonly tooltip?: string;
  readonly menuItems: (menu: RailMenu) => ReactNode;
  readonly onToggle?: () => void;
  readonly onSelect?: () => void;
  readonly onDragStart?: (event: DragEvent) => void;
  readonly onDragEnd?: () => void;
  readonly onDragOver?: (event: DragEvent) => void;
  readonly onDragLeave?: () => void;
  readonly onDrop?: (event: DragEvent) => void;
}) {
  return (
    <ContextMenu>
      <ContextMenuTrigger
        draggable
        render={<div style={railIndent(depth, 4)} />}
        className={cn(RAIL_ROW, refusesDrop && 'opacity-40', FOLDER_ROW_STATE[state])}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        onClick={onSelect}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}>
        <Button
          variant="ghost"
          size="icon-xs"
          aria-label={isExpanded ? 'Collapse folder' : 'Expand folder'}
          className="text-muted-foreground hover:text-foreground size-5 shrink-0"
          onClick={(e) => {
            e.stopPropagation();
            onToggle?.();
          }}>
          <ChevronRight
            className={cn('transition-transform duration-150', isExpanded && 'rotate-90')}
          />
        </Button>
        <FolderIcon className="size-3.5 shrink-0" />
        {rename ?? (
          <span title={tooltip ?? name} className="text-foreground flex-1 truncate">
            {name}
          </span>
        )}
        <RailInvestigationCount count={count} />
        <DropdownMenu>
          <DropdownMenuTrigger
            aria-label={`Actions for ${name}`}
            title={`Actions for ${name}`}
            render={
              <Button
                variant="ghost"
                size="icon-sm"
                className="text-foreground -my-1 size-6 shrink-0"
              />
            }
            onClick={(e) => {
              e.stopPropagation();
            }}>
            <MoreHorizontal className="size-4" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            {menuItems(DROPDOWN_MENU)}
          </DropdownMenuContent>
        </DropdownMenu>
      </ContextMenuTrigger>
      <ContextMenuContent className="w-52">{menuItems(CONTEXT_MENU)}</ContextMenuContent>
    </ContextMenu>
  );
}
